import { useMemo, useState } from 'react';
import { loadHeadwordIndex, searchHeadwords } from '../lib/data.js';
import { aggregateScore } from '../lib/progress.js';

function ThemeCard({ theme, modes, progress, onOpen }) {
  return (
    <button className="theme-card" type="button" onClick={() => onOpen(theme.id)}>
      <span className="theme-number">THEME {String(theme.number).padStart(2, '0')}</span>
      <span className="theme-mode-scores">
        {modes.map((mode) => {
          const score = aggregateScore(progress, theme.id, mode.id).score;
          return <span className={mode.className} key={mode.id}>{mode.emoji} {score}</span>;
        })}
      </span>
      <span className="theme-main">
        <span className="theme-emoji" aria-hidden="true">{theme.emoji}</span>
        <strong>{theme.title}</strong>
      </span>
      <span className="theme-footer">
        <span>{theme.subtopicCount} subtopics</span>
        <span aria-hidden="true">→</span>
      </span>
    </button>
  );
}

const GROUP_MODES = {
  'vocabulary-collocation': [
    { id: 'guided-practice', emoji: '📘', className: 'guided-chip' },
    { id: 'sentence-building', emoji: '✍️', className: 'building-chip' },
  ],
  'listening-spelling': [
    { id: 'listen-spell', emoji: '🎧', className: 'spell-chip' },
    { id: 'listen-for-details', emoji: '🔎', className: 'details-chip' },
  ],
};

export default function ThemeDirectory({ master, progress, onOpen, onOpenHeadword }) {
  const [query, setQuery] = useState('');
  const [headwordIndex, setHeadwordIndex] = useState(null);
  const [searchStatus, setSearchStatus] = useState('idle');
  const [searchError, setSearchError] = useState('');
  const groups = master.themeGroups.map((group) => ({
    ...group,
    themes: master.themes.filter((theme) => group.themeIds.includes(theme.id)),
  }));
  const results = useMemo(
    () => searchHeadwords(headwordIndex, query, 10),
    [headwordIndex, query],
  );

  async function prepareSearch() {
    if (headwordIndex || searchStatus === 'loading') return;
    setSearchStatus('loading');
    setSearchError('');
    try {
      setHeadwordIndex(await loadHeadwordIndex());
      setSearchStatus('ready');
    } catch (error) {
      setSearchError(error.message);
      setSearchStatus('error');
    }
  }

  function updateQuery(event) {
    setQuery(event.target.value);
    if (!headwordIndex) void prepareSearch();
  }

  function openFirstResult(event) {
    event.preventDefault();
    if (results[0]) onOpenHeadword(results[0]);
  }

  return (
    <main className="page theme-page">
      <section className="hero hero-centred theme-hero">
        <p className="eyebrow coral-eyebrow">SEVEN LEARNING THEMES</p>
        <h1>Build useful vocabulary, <em>one context at a time.</em></h1>
        <p className="hero-copy">
          Choose a theme to explore its subtopics. Your vocabulary and listening scores
          stay visible as you learn.
        </p>
      </section>

      <section aria-labelledby="theme-heading">
        <div className="directory-title-row theme-directory-title-row">
          <div>
            <p className="eyebrow coral-eyebrow">START HERE</p>
            <h2 id="theme-heading">Choose a theme</h2>
          </div>
          <div className="theme-directory-tools">
            <form className="headword-search" role="search" onSubmit={openFirstResult}>
              <label htmlFor="headword-search-input">Search a word</label>
              <span className="headword-search-field">
                <span aria-hidden="true">⌕</span>
                <input
                  id="headword-search-input"
                  type="search"
                  value={query}
                  placeholder="Search a headword…"
                  autoComplete="off"
                  aria-controls="headword-search-results"
                  aria-expanded={Boolean(query.trim())}
                  onFocus={prepareSearch}
                  onChange={updateQuery}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setQuery('');
                  }}
                />
              </span>
              {query.trim() && (
                <div className="headword-search-results" id="headword-search-results" aria-live="polite">
                  {searchStatus === 'loading' && <p>Loading headwords…</p>}
                  {searchError && <p>{searchError}</p>}
                  {searchStatus === 'ready' && !results.length && <p>No matching headword found.</p>}
                  {results.map((result) => (
                    <button
                      type="button"
                      key={`${result.themeId}/${result.subtopicId}/${result.headwordId}`}
                      onClick={() => onOpenHeadword(result)}
                    >
                      <span>
                        <strong>{result.displayText}</strong>
                        <small>{result.partOfSpeech}</small>
                      </span>
                      <span>{result.themeTitle} · {result.subtopicTitle} · {result.levelLabel}</span>
                    </button>
                  ))}
                </div>
              )}
            </form>
            <span className="theme-count">7 themes</span>
          </div>
        </div>
        {groups.map((group) => (
          <div className="theme-group" key={group.id}>
            <div className={`group-banner group-banner-${group.id}`}>{group.label}</div>
            <div className="theme-grid">
              {group.themes.map((theme) => (
                <ThemeCard
                  key={theme.id}
                  theme={{ ...theme, subtopicCount: theme.subtopics?.length ?? 0 }}
                  modes={GROUP_MODES[group.id]}
                  progress={progress}
                  onOpen={onOpen}
                />
              ))}
            </div>
          </div>
        ))}
      </section>
    </main>
  );
}
